import { Pipe, PipeTransform, SecurityContext } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";

@Pipe({
  name: "sanitizeHtml",
  pure: true
})
export class SanitizeHtmlPipe implements PipeTransform {
  private readonly ALLOWED_TAGS = [
    "b",
    "i",
    "strong",
    "em",
    "a",
    "img",
    "br",
    "span",
    "p",
    "ul",
    "li",
    "ol",
    "pre",
    "code"
  ];

  private readonly ALLOWED_ATTRIBUTES = {
    a: ["href", "target", "rel"],
    img: ["src", "alt", "width", "height"],
    span: ["class", "style"],
    p: ["class", "style"],
    "*": ["class"]
  };

  private readonly ALLOWED_STYLES = ["font-size"];

  constructor(private readonly sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    if (!value) {
      return "";
    }

    // First use Angular's built-in sanitizer to handle dangerous content
    const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, value);

    if (!sanitized) {
      return "";
    }

    // Parse and filter HTML to match backend allowed tags/attributes
    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitized, "text/html");

    this.cleanNode(doc.body);

    // Return the cleaned HTML as safe
    return this.sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
  }

  private cleanNode(node: Node): void {
    const nodesToRemove: Node[] = [];
    const childNodes = Array.from(node.childNodes);

    for (const child of childNodes) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const element = child as Element;
        const tagName = element.tagName.toLowerCase();

        if (!this.ALLOWED_TAGS.includes(tagName)) {
          // Tag not allowed - remove it but keep its children
          const parent = element.parentNode;
          while (element.firstChild) {
            parent.insertBefore(element.firstChild, element);
          }
          nodesToRemove.push(element);
        } else {
          // Clean attributes
          this.cleanAttributes(element);
          // Recursively clean children
          this.cleanNode(element);
        }
      } else if (child.nodeType === Node.TEXT_NODE) {
        // Text nodes are safe
        continue;
      } else {
        // Remove other node types (comments, etc.)
        nodesToRemove.push(child);
      }
    }

    // Remove marked nodes
    nodesToRemove.forEach(n => {
      if (n.parentNode) {
        n.parentNode.removeChild(n);
      }
    });
  }

  private cleanAttributes(element: Element): void {
    const tagName = element.tagName.toLowerCase();
    const allowedAttrs = [...(this.ALLOWED_ATTRIBUTES[tagName] || []), ...(this.ALLOWED_ATTRIBUTES["*"] || [])];

    // Get all attributes
    const attrs = Array.from(element.attributes);

    attrs.forEach(attr => {
      if (!allowedAttrs.includes(attr.name)) {
        element.removeAttribute(attr.name);
      } else if (attr.name === "style") {
        // Clean style attribute
        this.cleanStyles(element);
      } else if (attr.name === "href" && tagName === "a") {
        // Ensure safe href
        this.sanitizeHref(element);
      } else if (attr.name === "target" && tagName === "a") {
        // Ensure rel="noopener" for target="_blank"
        if (attr.value === "_blank" && !element.hasAttribute("rel")) {
          element.setAttribute("rel", "noopener");
        }
      }
    });
  }

  private cleanStyles(element: Element): void {
    const style = element.getAttribute("style");
    if (!style) {
      return;
    }

    const allowedStyles: string[] = [];
    const styleDeclarations = style.split(";");

    styleDeclarations.forEach(decl => {
      const [property] = decl.split(":").map(s => s.trim());
      if (this.ALLOWED_STYLES.includes(property)) {
        allowedStyles.push(decl.trim());
      }
    });

    if (allowedStyles.length > 0) {
      element.setAttribute("style", allowedStyles.join("; "));
    } else {
      element.removeAttribute("style");
    }
  }

  private sanitizeHref(element: Element): void {
    const href = element.getAttribute("href");
    if (!href) {
      return;
    }

    // Only allow http, https, and relative URLs
    const allowedProtocols = ["http:", "https:", "mailto:"];
    try {
      const url = new URL(href, window.location.origin);
      if (!allowedProtocols.includes(url.protocol)) {
        element.removeAttribute("href");
      }
    } catch {
      // Relative URL or invalid - keep relative URLs
      if (href.startsWith("javascript:") || href.startsWith("data:")) {
        element.removeAttribute("href");
      }
    }
  }
}
